import { useState } from 'react'
import type { Booking } from '../store/slices/bookingsSlice'

interface Props {
  bookings: Booking[]
}

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  Confirmed:   { bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-300' },
  Pending:     { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  'Checked In':  { bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300' },
  'Checked Out': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
  Cancelled:   { bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-300' },
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function parseDateLocal(dateStr: string): Date {
  // Parses YYYY-MM-DD without timezone shift
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export default function BookingCalendar({ bookings }: Props) {
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth()) // 0-based
  const [selected, setSelected] = useState<Booking | null>(null)

  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startOffset = firstDay.getDay() // 0 = Sunday
  const daysInMonth = lastDay.getDate()

  // Build a map: dateString (YYYY-MM-DD) -> Booking[]
  const bookingsByDay: Record<string, Booking[]> = {}
  bookings.forEach((b) => {
    if (!b.checkIn || !b.checkOut) return
    const start = parseDateLocal(b.checkIn)
    const end = parseDateLocal(b.checkOut)
    const cur = new Date(start)
    while (cur < end) {
      const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`
      if (!bookingsByDay[key]) bookingsByDay[key] = []
      bookingsByDay[key].push(b)
      cur.setDate(cur.getDate() + 1)
    }
  })

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1) }
    else setMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1) }
    else setMonth(m => m + 1)
  }

  const monthLabel = new Date(year, month, 1).toLocaleString('default', { month: 'long', year: 'numeric' })

  // Build grid cells: nulls for offset, then 1..daysInMonth
  const cells: (number | null)[] = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]
  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null)

  const colorFor = (status: string) =>
    STATUS_COLORS[status] ?? { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' }

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex flex-wrap gap-3 items-center">
        <span className="text-sm font-medium text-gray-600">Legend:</span>
        {Object.entries(STATUS_COLORS).map(([s, c]) => (
          <span key={s} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}>
            {s}
          </span>
        ))}
      </div>

      {/* Month navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-blue-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-900">
            ◀ Prev
          </button>
          <h3 className="text-lg font-bold text-secondary-900">{monthLabel}</h3>
          <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-900">
            Next ▶
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-100">
          {DAYS.map(d => (
            <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (day === null) {
              return <div key={`empty-${idx}`} className="min-h-[90px] border-b border-r border-gray-50 bg-gray-50/50" />
            }
            const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
            const dayBookings = bookingsByDay[dateKey] ?? []
            const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day

            return (
              <div
                key={dateKey}
                className={`min-h-[90px] border-b border-r border-gray-100 p-1 ${isToday ? 'bg-blue-50' : 'bg-white'}`}
              >
                <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white' : 'text-gray-700'}`}>
                  {day}
                </div>
                <div className="space-y-px overflow-hidden">
                  {dayBookings.slice(0, 3).map((b) => {
                    const c = colorFor(b.status)
                    return (
                      <button
                        key={`${dateKey}-${b.id}`}
                        onClick={() => setSelected(b)}
                        title={`${b.guestName} — ${b.unitType} | ${b.status}`}
                        className={`w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded border truncate ${c.bg} ${c.text} ${c.border} hover:opacity-80 transition-opacity`}
                      >
                        {b.guestName.split(' ')[0]} · {b.unitType || b.unitNumber}
                      </button>
                    )
                  })}
                  {dayBookings.length > 3 && (
                    <div className="text-[10px] text-gray-500 px-1">+{dayBookings.length - 3} more</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Booking detail panel */}
      {selected && (
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-secondary-900">{selected.guestName}</h3>
              <p className="text-sm text-gray-500">{selected.id}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${colorFor(selected.status).bg} ${colorFor(selected.status).text} ${colorFor(selected.status).border}`}>
                {selected.status}
              </span>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Email</p>
              <p className="font-medium">{selected.guestEmail}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Unit</p>
              <p className="font-medium">{selected.unitType} {selected.unitNumber ? `#${selected.unitNumber}` : ''}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Check-in</p>
              <p className="font-medium">{selected.checkIn}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Check-out</p>
              <p className="font-medium">{selected.checkOut}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Guests</p>
              <p className="font-medium">{selected.guests}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Total Amount</p>
              <p className="font-medium text-green-700">R{selected.totalAmount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Payment</p>
              <p className="font-medium capitalize">{selected.payment}</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(STATUS_COLORS).map(([status, c]) => {
          const count = bookings.filter(b => b.status === status).length
          return (
            <div key={status} className={`rounded-xl border p-4 text-center ${c.bg} ${c.border}`}>
              <div className={`text-2xl font-bold ${c.text}`}>{count}</div>
              <div className={`text-xs font-medium mt-1 ${c.text}`}>{status}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
